import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { logger } from "@/lib/logger";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  ArrowLeft, FileText, Loader2, RefreshCw, ChevronLeft, ChevronRight,
  AlertTriangle, ShoppingCart, Receipt,
} from "lucide-react";

const STATUSES = ["", "DRAFT", "SUBMITTED", "AUTHORISED", "PAID", "VOIDED", "DELETED"];
const STATUS_COLORS = {
  DRAFT: "bg-zinc-100 text-zinc-700 border-zinc-200",
  SUBMITTED: "bg-blue-50 text-blue-800 border-blue-200",
  AUTHORISED: "bg-amber-50 text-amber-800 border-amber-200",
  PAID: "bg-emerald-100 text-emerald-800 border-emerald-200",
  VOIDED: "bg-zinc-100 text-zinc-500 border-zinc-200",
  DELETED: "bg-red-50 text-red-700 border-red-200",
};

/**
 * Stage 2: Per-tenant Sales (ACCREC) + Bills (ACCPAY) viewer.
 * Tabs switch between sales invoices and supplier bills; same backend
 * endpoint, just a different `invoice_type` param.
 */
export default function XeroInvoicesPage() {
  const { tenantId } = useParams();
  const [tenant, setTenant] = useState(null);
  const [type, setType] = useState("ACCREC");
  const [status, setStatus] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ invoices: [], totals: null });
  const [loading, setLoading] = useState(false);

  const loadTenant = useCallback(async () => {
    try {
      const { data } = await api.get("/integrations/xero/tenants");
      setTenant((data || []).find((t) => t.tenant_id === tenantId)
        || { tenant_id: tenantId, tenant_name: tenantId });
    } catch (e) {
      logger.warn("[Xero] tenant lookup failed:", e?.response?.data || e);
    }
  }, [tenantId]);
  useEffect(() => { loadTenant(); }, [loadTenant]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ invoice_type: type, page: String(page) });
      if (status) params.set("status", status);
      if (fromDate) params.set("from_date", fromDate);
      if (toDate) params.set("to_date", toDate);
      const { data } = await api.get(
        `/integrations/xero/tenants/${tenantId}/invoices?${params}`);
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load invoices");
    } finally { setLoading(false); }
  }, [tenantId, type, status, page, fromDate, toDate]);
  useEffect(() => { load(); }, [load]);

  const switchType = (t) => { setType(t); setPage(1); setStatus(""); };
  const totals = data.totals || {};
  const isSales = type === "ACCREC";

  // Date range presets — set both from + to in one click.
  const applyPreset = (preset) => {
    const now = new Date();
    const iso = (d) => d.toISOString().slice(0, 10);
    let from = "", to = iso(now);
    if (preset === "30d") {
      const d = new Date(now); d.setDate(d.getDate() - 30); from = iso(d);
    } else if (preset === "tm") {
      from = iso(new Date(now.getFullYear(), now.getMonth(), 1));
    } else if (preset === "lm") {
      from = iso(new Date(now.getFullYear(), now.getMonth() - 1, 1));
      to = iso(new Date(now.getFullYear(), now.getMonth(), 0));
    } else if (preset === "tq") {
      const q = Math.floor(now.getMonth() / 3);
      from = iso(new Date(now.getFullYear(), q * 3, 1));
    } else if (preset === "ty") {
      from = iso(new Date(now.getFullYear(), 0, 1));
    } else if (preset === "ly") {
      from = iso(new Date(now.getFullYear() - 1, 0, 1));
      to = iso(new Date(now.getFullYear() - 1, 11, 31));
    } else if (preset === "all") {
      from = ""; to = "";
    }
    setFromDate(from); setToDate(to); setPage(1);
  };

  // Free-text client-side filter across the loaded page.
  const searchLower = search.trim().toLowerCase();
  const filteredInvoices = !searchLower
    ? (data.invoices || [])
    : (data.invoices || []).filter((i) =>
        [i.invoice_number, i.contact_name, i.reference, i.status]
          .map((v) => String(v || "").toLowerCase())
          .some((v) => v.includes(searchLower))
      );

  return (
    <div className="p-6 max-w-7xl" data-testid="xero-invoices-page">
      <Link to="/integrations/xero"
        className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-900 mb-3"
        data-testid="xero-invoices-back">
        <ArrowLeft className="h-3 w-3" /> Back to Xero
      </Link>

      <div className="flex items-center gap-3 mb-6">
        <div className="h-12 w-12 grid place-items-center rounded-sm bg-[#13B5EA]/10">
          <FileText className="h-6 w-6 text-[#13B5EA]" />
        </div>
        <div className="flex-1">
          <div className="label-overline">XERO · INVOICES &amp; BILLS</div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight">
            {tenant?.tenant_name || "…"}
          </h1>
        </div>
        <Button size="sm" variant="outline" className="rounded-sm"
          onClick={load} data-testid="xero-invoices-refresh">
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
        </Button>
      </div>

      {/* ── Type tabs ── */}
      <div className="flex gap-2 mb-4">
        <button type="button" onClick={() => switchType("ACCREC")}
          className={`px-4 h-10 rounded-sm border font-display font-bold text-sm inline-flex items-center gap-2 transition-colors ${
            isSales ? "bg-zinc-900 text-white border-zinc-900"
                    : "bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50"
          }`}
          data-testid="xero-invoices-tab-sales">
          <ShoppingCart className="h-4 w-4" /> Sales invoices
        </button>
        <button type="button" onClick={() => switchType("ACCPAY")}
          className={`px-4 h-10 rounded-sm border font-display font-bold text-sm inline-flex items-center gap-2 transition-colors ${
            !isSales ? "bg-zinc-900 text-white border-zinc-900"
                     : "bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50"
          }`}
          data-testid="xero-invoices-tab-bills">
          <Receipt className="h-4 w-4" /> Supplier bills
        </button>
      </div>

      {/* ── Totals pills (current page) ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <SummaryPill label="On page" value={totals.count ?? 0} />
        <SummaryPill label="Page total"
          value={fmtMoney(totals.total_amount)} />
        <SummaryPill label="Page due" value={fmtMoney(totals.total_due)}
          accent="text-amber-700" />
        <SummaryPill label="Overdue (page)"
          value={`${totals.overdue_count ?? 0} · ${fmtMoney(totals.overdue_amount)}`}
          accent={(totals.overdue_count ?? 0) > 0 ? "text-red-700" : ""} />
      </div>

      {/* ── Date range presets ── */}
      <div className="flex flex-wrap gap-1.5 mb-3" data-testid="xero-invoices-presets">
        {[
          ["all", "All"],
          ["30d", "Last 30 days"],
          ["tm", "This month"],
          ["lm", "Last month"],
          ["tq", "This quarter"],
          ["ty", "This year"],
          ["ly", "Last year"],
        ].map(([k, label]) => (
          <button key={k} type="button" onClick={() => applyPreset(k)}
            className="px-2.5 h-7 rounded-sm border border-zinc-200 bg-white text-[10px] uppercase tracking-[0.12em] font-bold text-zinc-700 hover:border-[#13B5EA] hover:text-[#13B5EA]"
            data-testid={`xero-invoices-preset-${k}`}>
            {label}
          </button>
        ))}
      </div>

      {/* ── Filters + pagination ── */}
      <Card className="rounded-sm border-zinc-200 mb-4">
        <CardContent className="p-4 flex flex-wrap items-end gap-3">
          <label className="text-xs uppercase tracking-[0.15em] text-zinc-500">
            Status
            <select value={status}
              onChange={(e) => { setStatus(e.target.value); setPage(1); }}
              className="mt-1 block h-9 w-44 border border-zinc-200 rounded-sm px-2 text-xs bg-white"
              data-testid="xero-invoices-status">
              <option value="">All statuses</option>
              {STATUSES.filter(Boolean).map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </label>
          <label className="text-xs uppercase tracking-[0.15em] text-zinc-500">
            From
            <Input type="date" value={fromDate}
              onChange={(e) => { setFromDate(e.target.value); setPage(1); }}
              className="mt-1 h-9 text-xs font-mono"
              data-testid="xero-invoices-from" />
          </label>
          <label className="text-xs uppercase tracking-[0.15em] text-zinc-500">
            To
            <Input type="date" value={toDate}
              onChange={(e) => { setToDate(e.target.value); setPage(1); }}
              className="mt-1 h-9 text-xs font-mono"
              data-testid="xero-invoices-to" />
          </label>
          <label className="text-xs uppercase tracking-[0.15em] text-zinc-500 flex-1 min-w-[180px]">
            Search
            <Input type="search" value={search} placeholder="Number, contact, reference…"
              onChange={(e) => setSearch(e.target.value)}
              className="mt-1 h-9 text-xs"
              data-testid="xero-invoices-search" />
          </label>
          <Button size="sm" variant="ghost" className="rounded-sm"
            disabled={page <= 1 || loading}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            data-testid="xero-invoices-prev">
            <ChevronLeft className="h-3.5 w-3.5 mr-1" /> Newer
          </Button>
          <Badge className="rounded-sm bg-zinc-100 text-zinc-700 border-zinc-200">Page {page}</Badge>
          <Button size="sm" variant="ghost" className="rounded-sm"
            disabled={loading || (data.invoices || []).length < 100}
            onClick={() => setPage((p) => p + 1)}
            data-testid="xero-invoices-next">
            Older <ChevronRight className="h-3.5 w-3.5 ml-1" />
          </Button>
        </CardContent>
      </Card>

      {/* ── Table ── */}
      <Card className="rounded-sm border-zinc-200">
        <CardContent className="p-0">
          <div className="grid grid-cols-[110px_1fr_110px_110px_110px_110px_70px] gap-2 px-3 py-2 bg-zinc-50 border-b border-zinc-200 text-[10px] uppercase tracking-[0.15em] font-bold text-zinc-600">
            <span>Number</span>
            <span>{isSales ? "Customer" : "Supplier"}</span>
            <span>Date</span>
            <span>Due</span>
            <span className="text-right">Total</span>
            <span className="text-right">Due</span>
            <span className="text-right">Status</span>
          </div>
          {loading ? (
            <div className="p-8 text-center"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
          ) : filteredInvoices.length === 0 ? (
            <div className="p-8 text-center text-sm italic text-zinc-500"
              data-testid="xero-invoices-empty">
              {searchLower ? "No invoices match this search." : "No invoices match these filters."}
            </div>
          ) : (
            filteredInvoices.map((i) => (
              <Link key={i.invoice_id}
                to={`/integrations/xero/tenants/${tenantId}/invoices/${i.invoice_id}`}
                className={`grid grid-cols-[110px_1fr_110px_110px_110px_110px_70px] gap-2 px-3 py-2 border-b border-zinc-100 last:border-b-0 text-sm items-center hover:bg-[#13B5EA]/5 transition-colors ${
                  i.is_overdue ? "bg-red-50/30" : ""
                }`}
                data-testid={`xero-invoice-${i.invoice_id}`}>
                <span className="font-mono text-xs truncate">
                  {i.invoice_number || "—"}
                </span>
                <span className="truncate">
                  {i.contact_name || <span className="italic text-zinc-500">No contact</span>}
                  {i.reference && (
                    <span className="text-[10px] text-zinc-500 font-mono ml-2">{i.reference}</span>
                  )}
                </span>
                <span className="font-mono text-[11px]">{fmtDate(i.date)}</span>
                <span className={`font-mono text-[11px] ${
                  i.is_overdue ? "text-red-700 font-semibold" : ""
                }`}>
                  {fmtDate(i.due_date)}
                  {i.is_overdue && <AlertTriangle className="h-3 w-3 inline ml-1" />}
                </span>
                <span className="font-mono text-right">
                  {fmtMoney(i.total)} <span className="text-[9px] text-zinc-500">{i.currency}</span>
                </span>
                <span className={`font-mono text-right ${
                  Number(i.amount_due) > 0 ? "text-amber-700 font-semibold" : "text-zinc-400"
                }`}>
                  {fmtMoney(i.amount_due)}
                </span>
                <span className="text-right">
                  <Badge variant="outline"
                    className={`rounded-sm text-[9px] ${STATUS_COLORS[i.status] || ""}`}>
                    {i.status}
                  </Badge>
                </span>
              </Link>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function SummaryPill({ label, value, accent }) {
  return (
    <div className="border border-zinc-200 rounded-sm p-3 bg-white">
      <div className="label-overline text-[9px]">{label}</div>
      <div className={`font-display font-bold text-xl mt-1 ${accent || ""}`}>{value}</div>
    </div>
  );
}

function fmtDate(s) {
  if (!s) return "—";
  const str = String(s);
  // Xero returns dates as `/Date(1424304000000+0000)/` — extract epoch.
  const m = str.match(/\/Date\((-?\d+)/);
  if (m) {
    const d = new Date(parseInt(m[1], 10));
    if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  }
  return str.slice(0, 10) || "—";
}

function fmtMoney(n) {
  if (n === null || n === undefined || n === "") return "—";
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  return v.toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
}
